import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  OPERATIONS,
  VALUE_MATCH,
} from '../../../../hooks/filters/interfaces/filter-selected-value.interface';
import { useAppFilterSelectedValue } from '../../../../hooks/filters/use-app-filter-selected-value.hook';
import { useAppSimpleFilterSelectedValue } from '../../../filters/hooks/common.hook';
import { FILTER_NAMES } from '../../../filters/hooks/filter-names.constants';
import { DragBoxPluginChart } from '../plugins/draw-box.plugin';
import { DateZoomRange } from './use-zoom.hook';

const matchStrategy = {
  operation: OPERATIONS.AND,
  valueMatch: VALUE_MATCH.RANGE,
};

export const useZoomDate = () => {
  // Capture Date hierarchy (Date View) filter value to know how to set up date ranges.
  const dateHierValue = useAppSimpleFilterSelectedValue(FILTER_NAMES.DATEHIER);
  const dateHier: string = useMemo(() => {
    if (dateHierValue && dateHierValue.values.length > 0) {
      return dateHierValue.values[0].value;
    } else {
      return '';
    }
  }, [dateHierValue]);

  const [zoom, setZoom] = useState<DateZoomRange | undefined>();
  const [resetZoom, setResetZoomCallback] = useState<
    DragBoxPluginChart | undefined
  >();

  const { selectedValue: dateZoomValue, setSelectedValue: setDateZoomValue } =
    useAppFilterSelectedValue(FILTER_NAMES.DATEZOOM);

  useEffect(() => {
    // zoom object is modified out side this hook.
    if (zoom) {
      let startDate: Date = zoom.startDate;
      let endDate: Date = zoom.endDate;
      switch (dateHier) {
        case 'Week':
          startDate = zoom.startDate;
          endDate = zoom.endDate;
          break;
        case 'Quarter':
        case 'Month':
          startDate = moment(zoom.startDate).startOf('month').toDate();
          endDate = moment(zoom.endDate).endOf('month').toDate();
          break;
        case 'Year':
          startDate = moment(zoom.startDate).startOf('year').toDate();
          endDate = moment(zoom.endDate).endOf('year').toDate();
          break;
      }
      // This will update dateZoom filter.
      setDateZoomValue({
        id: undefined,
        values: [
          {
            value: startDate,
            label: '',
          },
          {
            value: endDate,
            label: '',
          },
        ],
        matchStrategy,
      });
    } else {
      // When there is no range, we have to clean the filter out.
      setDateZoomValue({
        id: undefined,
        values: [],
        matchStrategy,
      });
    }
  }, [zoom, setDateZoomValue, dateHier]);
  // This callback funtion is helpful for reset a chart component.
  const resetZoomFunction = useCallback(() => {
    if (resetZoom) {
      resetZoom.resetRange();
    }
  }, [resetZoom]);

  return {
    // Provides reset zoom functionality
    resetZoomFunction,
    // Provides date ranges.
    zoom,
    // Provides setter to change date ranges.
    setZoom,
    // Provides setter to load chart object.
    setResetZoomCallback,
    // Date range filter value.
    dateZoomValue,
  };
};
